import {isEqual} from 'lodash';

import {areArraysShallowEqual} from '../../../../libs/shared/helpers/src/utilities/arrayHelpers';
import {
  AutolinkerModel,
  ReferenceFormatModel,
  UnsavedAutolinkerModel,
} from '../../../models/api/autolinkerModel';
import {ExpressionState} from '../../editors/expressions/expressionState';
import {CustomFields} from '../commons/customFieldsModel';
import {RegexCharacterClassesEnum, StringOrTemplate} from '../commons/stringTemplateModel';
import {Expression, serializeExpression} from '../expressions/expressionModel';
import {AutolinkerResult} from '../expressions/infer/expressionResultModel';
import {computeAutolinkerEditorFormErrors} from './autolinkerEditorFormErrors';

const SINGLE_WORD_BOUNDARY: StringOrTemplate = {
  template: [{char: RegexCharacterClassesEnum.WORD_BOUNDARY}],
};

export interface AutolinkerState {
  referenceFormat: ReferenceFormatModel;
  description?: string;
  urlBuilder: StringOrTemplate;
}

export interface AutolinkerEditorState extends ExpressionState {
  autolinkerState: AutolinkerState;
  initialAutolinker?: AutolinkerModel;
}

export const AutolinkerEditorState = {
  get default(): AutolinkerEditorState {
    return {
      ...ExpressionState.makeDefault(AutolinkerResult),
      autolinkerState: {
        referenceFormat: {
          pattern: '',
          before: SINGLE_WORD_BOUNDARY,
          after: SINGLE_WORD_BOUNDARY,
        },
        description: '',
        urlBuilder: '',
      },
    };
  },

  createInitialState(
    initialAutolinker: AutolinkerModel | undefined,
    customFields: CustomFields,
  ): AutolinkerEditorState {
    if (!initialAutolinker) {
      return {...this.default};
    }

    return {
      ...this.default,
      ...ExpressionState.createInitialState(
        initialAutolinker.expressions,
        customFields,
        AutolinkerResult,
        {},
      ),
      initialAutolinker,
      autolinkerState: {
        referenceFormat: initialAutolinker.referenceFormat,
        description: initialAutolinker.description,
        urlBuilder: initialAutolinker.urlBuilder,
      },
    };
  },

  toAutolinker(state: AutolinkerEditorState): UnsavedAutolinkerModel {
    const {autolinkerState, expressions} = state;

    return {
      expressions: expressions.map(serializeExpression),
      referenceFormat: autolinkerState.referenceFormat,
      description: autolinkerState.description,
      urlBuilder: autolinkerState.urlBuilder,
    };
  },

  hasDiff(a: AutolinkerEditorState, b: AutolinkerEditorState) {
    if (a === b) {
      return false;
    }

    const aState = a.autolinkerState;
    const bState = b.autolinkerState;

    if (!isEqual(aState.referenceFormat, bState.referenceFormat)) {
      return true;
    }

    if (aState.description !== bState.description) {
      return true;
    }

    if (!isEqual(aState.urlBuilder, bState.urlBuilder)) {
      return true;
    }

    if (!areArraysShallowEqual(a.expressions, b.expressions, Expression.equals)) {
      return true;
    }

    return false;
  },

  computeFormErrors(state: AutolinkerEditorState) {
    return computeAutolinkerEditorFormErrors(state);
  },
};
